import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { btnOutline, btnPrimary } from "@/components/kit";
import api from "@/lib/api";
import { track } from "@/lib/track";
import { toast } from "sonner";
import { CheckCircle2, Clock, Phone, CalendarCheck, FileText, Layers } from "lucide-react";
import { CONTACT } from "@/data/site";

const SCENARIOS = [
  { name: "Essentiel", text: "Le format juste et efficace : l'essentiel de vos prestations, optimisé pour votre budget." },
  { name: "Signature", text: "L'équilibre Désir Events : scénographie soignée, prestations complètes, coordination renforcée." },
  { name: "Prestige", text: "Le format d'exception : lieu marquant, expérience invités premium, service sur-mesure." },
];
const SLOTS = [{ id: "matin", label: "Matin (9h-12h)" }, { id: "apres-midi", label: "Après-midi (14h-18h)" }, { id: "soiree", label: "Soirée (18h-20h)" }];
const fmt = (n) => n != null ? n.toLocaleString("fr-FR") : null;

function CallbackBooking({ dossierId, existing }) {
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [done, setDone] = useState(existing || "");
  const [loading, setLoading] = useState(false);

  const book = async () => {
    if (!date || !slot) { toast.error("Choisissez une date et un créneau."); return; }
    setLoading(true);
    try {
      const { data } = await api.post(`/submissions/${dossierId}/callback`, { callback_slot: `${date} · ${slot}` });
      setDone(data.callback_slot);
      toast.success("Créneau de rappel réservé.");
    } catch { toast.error("Impossible de réserver ce créneau."); }
    finally { setLoading(false); }
  };

  if (done) {
    return (
      <p className="flex items-center gap-2 text-sm font-medium text-ink" data-testid="merci-callback-done">
        <CheckCircle2 size={16} className="text-lime-dim" /> Rappel réservé : {done}. Votre chef de projet vous appellera.
      </p>
    );
  }
  return (
    <div data-testid="merci-callback-booking">
      <div className="grid gap-4 sm:grid-cols-2">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
          className="rounded-lg border border-ink/15 bg-white px-4 py-2.5 text-sm text-ink" data-testid="merci-callback-date" />
        <div className="flex flex-wrap gap-2">
          {SLOTS.map((s) => (
            <button key={s.id} type="button" onClick={() => setSlot(s.label)} aria-pressed={slot === s.label}
              data-testid={`merci-callback-slot-${s.id}`}
              className={`rounded-full border px-3.5 py-2 text-xs transition-colors ${slot === s.label ? "border-lime bg-lime text-ink" : "border-ink/15 text-ink/60 hover:text-ink"}`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>
      <button onClick={book} disabled={loading} className={`${btnPrimary} mt-4 disabled:opacity-60`} data-testid="merci-callback-book-btn">
        {loading ? "Réservation…" : "Réserver mon échange téléphonique"} <CalendarCheck size={15} />
      </button>
    </div>
  );
}

export default function MerciDevis() {
  const [params] = useSearchParams();
  const id = params.get("id");
  const [summary, setSummary] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (!id) return;
    api.get(`/submissions/${id}/summary`).then((r) => setSummary(r.data)).catch(() => setMissing(true));
  }, [id]);

  const resumeRows = summary ? [
    ["Type d'événement", summary.service],
    ["Ville", summary.city],
    ["Date", summary.event_date || summary.date_period],
    ["Horaires", summary.schedule],
    ["Invités", summary.participants ? `${summary.participants} personnes` : null],
    ["Budget indiqué", summary.budget],
    ["Prestations", (summary.prestations || []).join(", ")],
  ].filter(([, v]) => v) : [];

  return (
    <>
      <Helmet>
        <title>Merci — Désir Events</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <PageHero eyebrow="Brief bien reçu" title={<>Merci pour<br /><span className="italic text-lime-dim">votre confiance.</span></>} />

      <section className="mx-auto max-w-[900px] px-5 py-16 md:px-10 md:py-24" data-testid="merci-devis">
        {/* En-tête + dossier */}
        <Reveal>
          <div className="rounded-2xl border border-lime-dim/40 bg-lime/10 p-8 text-center md:p-10">
            <CheckCircle2 size={40} className="mx-auto text-lime-dim" />
            <h1 className="mt-4 font-serif text-3xl text-ink md:text-4xl">Votre brief est entre nos mains.</h1>
            {summary?.dossier && (
              <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 text-sm font-medium text-ink" data-testid="merci-dossier">
                <FileText size={14} className="text-lime-dim" /> Dossier {summary.dossier}
              </p>
            )}
            <p className="mx-auto mt-4 max-w-lg text-ink/70">
              Échange de cadrage sous 24 h ouvrées, puis <strong>trois scénarios budgétaires</strong> adaptés à votre projet —
              Essentiel, Signature et Prestige — construits par votre chef de projet Désir Events.
            </p>
          </div>
        </Reveal>

        {summary && (
          <>
            {/* Résumé du projet */}
            <Reveal delay={1}>
              <div className="mt-8 rounded-2xl border border-ink/10 bg-white p-8" data-testid="merci-resume">
                <h2 className="font-serif text-2xl text-ink">Votre projet en résumé</h2>
                <dl className="mt-5 grid gap-x-8 gap-y-3 sm:grid-cols-2">
                  {resumeRows.map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-4 border-b border-ink/5 pb-2 text-sm">
                      <dt className="text-ink/45">{k}</dt><dd className="text-right font-medium text-ink">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </Reveal>

            {/* Fourchette budgétaire immédiate */}
            <Reveal delay={2}>
              <div className="mt-8 rounded-2xl border border-ink/10 bg-ink p-8 text-white" data-testid="merci-estimation">
                <span className="text-[11px] uppercase tracking-[0.2em] text-lime">Première estimation indicative</span>
                {summary.estimate_low != null ? (
                  <p className="mt-3 font-serif text-4xl md:text-5xl" data-testid="merci-estimation-range">
                    {fmt(summary.estimate_low)} € – {fmt(summary.estimate_high)} € <span className="text-xl text-white/60">HT</span>
                  </p>
                ) : (
                  <p className="mt-3 font-serif text-2xl" data-testid="merci-estimation-range">Chiffrage sur étude</p>
                )}
                <ul className="mt-4 space-y-1.5">
                  {(summary.estimate_notes || []).map((n) => (
                    <li key={n} className="text-xs leading-relaxed text-white/55">{n}</li>
                  ))}
                </ul>
                <p className="mt-4 text-xs text-white/45">
                  Fourchette indicative calculée sur vos réponses (format, invités, prestations, logistique, coordination, imprévus).
                  Le chiffrage ferme suit l'échange de cadrage avec votre chef de projet.
                </p>
              </div>
            </Reveal>

            {/* Reste à confirmer */}
            {summary.to_confirm?.length > 0 && (
              <Reveal delay={3}>
                <div className="mt-8 rounded-2xl border border-ink/10 bg-white p-8" data-testid="merci-a-confirmer">
                  <h2 className="font-serif text-2xl text-ink">Ce que nous confirmerons ensemble</h2>
                  <ul className="mt-4 space-y-2.5">
                    {summary.to_confirm.map((t) => (
                      <li key={t} className="flex items-start gap-2.5 text-sm text-ink/65">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-lime-dim" />{t}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            )}

            {/* Les 3 scénarios Désir Events */}
            <Reveal delay={4}>
              <div className="mt-8" data-testid="merci-scenarios">
                <h2 className="flex items-center gap-3 font-serif text-2xl text-ink"><Layers size={20} className="text-lime-dim" /> Vos 3 scénarios, signés Désir Events</h2>
                <p className="mt-2 text-sm text-ink/55">
                  Après l'échange de cadrage, votre chef de projet construit trois versions de votre événement — c'est toujours la même équipe qui organise et coordonne, de A à Z.
                </p>
                <div className="mt-6 grid gap-4 sm:grid-cols-3">
                  {SCENARIOS.map((s) => (
                    <div key={s.name} className="rounded-2xl border border-ink/10 bg-white p-6" data-testid={`merci-scenario-${s.name.toLowerCase()}`}>
                      <p className="font-serif text-xl text-ink">{s.name}</p>
                      <p className="mt-2 text-xs leading-relaxed text-ink/55">{s.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            {/* Réservation d'un échange */}
            <Reveal delay={5}>
              <div className="mt-8 rounded-2xl border border-lime-dim/40 bg-lime/5 p-8" data-testid="merci-rdv">
                <h2 className="font-serif text-2xl text-ink">Gagnez du temps : réservez votre échange</h2>
                <p className="mt-2 text-sm text-ink/55">Choisissez un créneau — votre chef de projet vous rappelle au moment convenu.</p>
                <div className="mt-5">
                  <CallbackBooking dossierId={summary.id} existing={summary.callback_slot} />
                </div>
              </div>
            </Reveal>
          </>
        )}

        {(!id || missing) && (
          <Reveal delay={1}>
            <p className="mt-8 text-center text-sm text-ink/55" data-testid="merci-generique">
              Un chef de projet étudie votre demande. Première réponse sous 24 h ouvrées.
            </p>
          </Reveal>
        )}

        <Reveal delay={6}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <a href={`tel:${CONTACT.phoneMobile.replace(/\s/g, "")}`} onClick={() => track("click_phone")} className={btnOutline} data-testid="merci-call-btn">
              <Phone size={15} /> Une urgence ? {CONTACT.phoneMobile}
            </a>
            <Link to="/nos-realisations" className={btnOutline}>Découvrir nos réalisations</Link>
          </div>
          <p className="mt-6 flex items-center justify-center gap-2 text-sm text-ink/55">
            <Clock size={15} className="text-lime-dim" /> Échange de cadrage sous 24 h ouvrées
          </p>
        </Reveal>
      </section>
    </>
  );
}
